import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Lock, LogOut, Search, Mail, Phone, Building2, MapPin, Package, ShieldCheck,
  CheckCircle2, Clock, Archive, Trash2, RefreshCw, ExternalLink, FileText,
  Inbox, FileCode2, Download, Paperclip, RotateCcw, Save, ArrowLeft, BarChart3,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import Analytics from '../components/Analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: { bg: 'bg-[#f7a11a]', text: 'text-slate-900', label: 'NEW', icon: Clock },
  replied: { bg: 'bg-emerald-600', text: 'text-white', label: 'REPLIED', icon: CheckCircle2 },
  archived: { bg: 'bg-slate-500', text: 'text-white', label: 'ARCHIVED', icon: Archive }
};

// ============ 2FA LOGIN ============
const LoginGate = ({ onLogin }) => {
  const [step, setStep] = useState(1); // 1 = password, 2 = OTP
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [sentTo, setSentTo] = useState('');
  const [emailSent, setEmailSent] = useState(true);
  const [codeHint, setCodeHint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login-request`, { password });
      setRequestId(res.data.request_id);
      setSentTo(res.data.sent_to);
      setEmailSent(res.data.email_sent);
      setCodeHint(res.data.code_hint);
      setCountdown(60);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid password.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login-verify`, { request_id: requestId, code });
      localStorage.setItem('udaan_admin_token', res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md bg-white shadow-2xl">
        <div className="bg-[#0b2545] p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#f7a11a] flex items-center justify-center">
              <span className="text-slate-900 font-black text-lg">U</span>
            </div>
            <div>
              <div className="text-white font-bold">UDAAN</div>
              <div className="text-white/60 text-[10px] tracking-[0.3em] uppercase">Admin Console</div>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-3">
            <div className={`h-1.5 flex-1 ${step >= 1 ? 'bg-[#f7a11a]' : 'bg-white/20'}`} />
            <div className={`h-1.5 flex-1 ${step >= 2 ? 'bg-[#f7a11a]' : 'bg-white/20'}`} />
          </div>
          <h1 className="mt-6 text-2xl font-black text-white">
            {step === 1 ? 'Sign in to continue' : 'Verify your identity'}
          </h1>
          <p className="mt-2 text-white/60 text-sm">
            {step === 1 ? 'Step 1 of 2 · Enter your admin password.' : `Step 2 of 2 · Enter the 6-digit code sent to ${sentTo}.`}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={requestCode} className="p-8 space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Password</label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]"
                  placeholder="••••••••" autoFocus required
                />
              </div>
            </div>
            {error && <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-[#0b2545] hover:bg-[#0b2545]/90 text-white h-12 rounded-none font-semibold tracking-widest text-sm">
              {loading ? 'SENDING CODE...' : 'CONTINUE'}
            </Button>
            <div className="pt-2 text-center">
              <Link to="/" className="text-xs text-slate-500 hover:text-[#f7a11a]">← Back to website</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="p-8 space-y-5">
            <div className="flex items-start gap-3 p-4 bg-[#f7a11a]/10 border border-[#f7a11a]/30">
              <ShieldCheck className="w-5 h-5 text-[#f7a11a] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-700 leading-relaxed">
                {emailSent
                  ? <>Code emailed to <strong>{sentTo}</strong>. It expires in 10 minutes.</>
                  : <>Email delivery failed. Your code starts with <strong className="font-mono">{codeHint}</strong> (check backend logs).</>
                }
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">6-digit code</label>
              <Input
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                className="mt-2 h-14 text-center text-2xl tracking-[0.5em] rounded-none border-slate-300 font-mono focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]"
                placeholder="000000" autoFocus required
              />
            </div>
            {error && <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
            <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-[#0b2545] hover:bg-[#0b2545]/90 text-white h-12 rounded-none font-semibold tracking-widest text-sm">
              {loading ? 'VERIFYING...' : 'VERIFY & SIGN IN'}
            </Button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => { setStep(1); setCode(''); setError(''); }} className="text-slate-500 hover:text-[#f7a11a] inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <button type="button" disabled={countdown > 0 || loading} onClick={() => requestCode()} className="text-[#f7a11a] hover:underline disabled:text-slate-400 disabled:no-underline">
                {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============ QUOTE DETAIL ============
const QuoteDetail = ({ quote, token, onUpdate, onClose }) => {
  const { toast } = useToast();
  const [notes, setNotes] = useState(quote.notes || '');
  const [saving, setSaving] = useState(false);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const update = async (updates) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/quotes/${quote.id}`, updates, authHeaders);
      onUpdate(res.data);
      toast({ title: 'Quote updated' });
    } catch (err) {
      toast({ title: 'Update failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm('Delete this quote permanently?')) return;
    try {
      await axios.delete(`${API}/admin/quotes/${quote.id}`, authHeaders);
      toast({ title: 'Quote deleted' });
      onClose(true);
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const downloadAttachment = async (filename) => {
    try {
      const res = await axios.get(`${API}/admin/quotes/${quote.id}/attachments/${encodeURIComponent(filename)}`, {
        ...authHeaders, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };

  const cfg = statusColors[quote.status] || statusColors.new;
  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => onClose(false)}>
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0b2545] text-white p-6 flex items-start justify-between gap-4 z-10">
          <div>
            <div className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-widest ${cfg.bg} ${cfg.text}`}>
              <cfg.icon className="w-3 h-3" /> {cfg.label}
            </div>
            <h2 className="mt-3 text-2xl font-black">{quote.name}</h2>
            <div className="mt-1 text-white/70 text-xs">Received {new Date(quote.timestamp).toLocaleString()} · ID: {quote.id.slice(0,8).toUpperCase()}</div>
          </div>
          <button onClick={() => onClose(false)} className="text-white/60 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <a href={`mailto:${quote.email}`} className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 hover:border-[#f7a11a]">
              <Mail className="w-5 h-5 text-[#f7a11a] flex-shrink-0 mt-0.5" />
              <div className="min-w-0"><div className="text-[10px] uppercase tracking-widest text-slate-500">Email</div><div className="font-semibold text-slate-900 truncate">{quote.email}</div></div>
            </a>
            <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200">
              <Phone className="w-5 h-5 text-[#f7a11a] flex-shrink-0 mt-0.5" />
              <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Phone</div><div className="font-semibold text-slate-900">{quote.phone || '—'}</div></div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200">
              <Building2 className="w-5 h-5 text-[#f7a11a] flex-shrink-0 mt-0.5" />
              <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Company</div><div className="font-semibold text-slate-900">{quote.company || '—'}</div></div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200">
              <MapPin className="w-5 h-5 text-[#f7a11a] flex-shrink-0 mt-0.5" />
              <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Country</div><div className="font-semibold text-slate-900">{quote.country || '—'}</div></div>
            </div>
          </div>

          <div className="p-4 bg-[#f7a11a]/10 border-l-4 border-[#f7a11a]">
            <div className="flex items-center gap-2"><Package className="w-4 h-4 text-[#f7a11a]" /><span className="text-[10px] uppercase tracking-widest text-slate-500">Product Interest</span></div>
            <div className="mt-1 font-bold text-slate-900">{quote.product || 'General inquiry'}</div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Project Details</div>
            <div className="p-4 bg-slate-50 border border-slate-200 text-slate-800 leading-relaxed whitespace-pre-wrap">{quote.message}</div>
          </div>

          {quote.attachments && quote.attachments.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> Attachments ({quote.attachments.length})</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {quote.attachments.map((a) => (
                  <button key={a.filename} onClick={() => downloadAttachment(a.filename)} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 hover:border-[#f7a11a] text-left w-full">
                    <FileText className="w-4 h-4 text-[#f7a11a] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">{a.filename}</div>
                      <div className="text-[11px] text-slate-500">{fmtSize(a.size)}</div>
                    </div>
                    <Download className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Internal Notes</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-none border-slate-300" placeholder="Add follow-up notes..." />
            <Button onClick={() => update({ notes })} disabled={saving} className="mt-2 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-none text-xs h-9 px-4 font-semibold">Save notes</Button>
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
            <Button onClick={() => update({ status: 'replied' })} disabled={saving || quote.status === 'replied'} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none h-10 font-semibold text-xs tracking-widest">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> MARK REPLIED
            </Button>
            <Button onClick={() => update({ status: 'new' })} disabled={saving || quote.status === 'new'} className="bg-[#f7a11a] hover:bg-[#e69108] text-slate-900 rounded-none h-10 font-semibold text-xs tracking-widest">
              <Clock className="w-4 h-4 mr-1.5" /> REOPEN
            </Button>
            <Button onClick={() => update({ status: 'archived' })} disabled={saving || quote.status === 'archived'} className="bg-slate-500 hover:bg-slate-600 text-white rounded-none h-10 font-semibold text-xs tracking-widest">
              <Archive className="w-4 h-4 mr-1.5" /> ARCHIVE
            </Button>
            <a href={`mailto:${quote.email}?subject=Re: RFQ ${quote.id.slice(0,8).toUpperCase()}`} className="inline-flex items-center gap-1.5 px-4 h-10 bg-[#0b2545] text-white text-xs font-semibold tracking-widest hover:bg-[#0b2545]/90">
              <ExternalLink className="w-4 h-4" /> REPLY VIA EMAIL
            </a>
            <Button onClick={del} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 rounded-none h-10 font-semibold text-xs tracking-widest ml-auto">
              <Trash2 className="w-4 h-4 mr-1.5" /> DELETE
            </Button>
          </div>

          <div className="text-[11px] text-slate-500 flex items-center gap-2">
            <FileText className="w-3 h-3" /> Email notification sent to inbox: <strong className={quote.email_sent ? 'text-emerald-600' : 'text-red-600'}>{quote.email_sent ? 'YES' : 'NO'}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ TEMPLATES ============
const TEMPLATE_META = {
  rfq_notification: { title: 'RFQ Notification (to you)', desc: 'Sent to your inbox whenever a new RFQ is submitted.', vars: ['name','email','phone','company','country','product','message','timestamp','id','short_id','attachments_block'] },
  customer_confirmation: { title: 'Customer Confirmation', desc: 'Auto-reply sent to the buyer confirming their RFQ was received.', vars: ['name','short_id'] },
  admin_otp: { title: 'Admin 2FA Login Code', desc: 'Two-factor code emailed every time you sign in to admin.', vars: ['code'] }
};

const TemplateEditor = ({ token }) => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState({});
  const [selected, setSelected] = useState('rfq_notification');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/templates`, auth);
      setTemplates(res.data);
      const t = res.data[selected];
      if (t) { setSubject(t.subject); setHtml(t.html); }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const t = templates[selected];
    if (t) { setSubject(t.subject); setHtml(t.html); }
  }, [selected, templates]);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/templates/${selected}`, { key: selected, subject, html }, auth);
      toast({ title: 'Template saved', description: 'Future emails will use this template.' });
      load();
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset this template to the default?')) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/templates/${selected}/reset`, {}, auth);
      toast({ title: 'Reset to default' });
      load();
    } finally { setLoading(false); }
  };

  const insertVar = (v) => {
    const el = document.getElementById('tpl-html');
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const insert = `{{${v}}}`;
    setHtml(html.slice(0, s) + insert + html.slice(e));
    setTimeout(() => { el.focus(); el.setSelectionRange(s + insert.length, s + insert.length); }, 10);
  };

  const meta = TEMPLATE_META[selected];
  const currentTpl = templates[selected];

  return (
    <div className="bg-white border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2">
        {Object.keys(TEMPLATE_META).map((k) => (
          <button key={k} onClick={() => setSelected(k)}
            className={`px-4 py-2 text-xs font-bold tracking-widest uppercase transition-colors ${selected === k ? 'bg-[#0b2545] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
            {TEMPLATE_META[k].title}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-0 min-h-[600px]">
        <div className="lg:col-span-3 p-6 border-r border-slate-200 space-y-4">
          <div>
            <div className="text-lg font-black text-slate-900">{meta.title}</div>
            <div className="text-sm text-slate-600">{meta.desc}</div>
            {currentTpl && !currentTpl.is_default && (
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-emerald-700 bg-emerald-100 px-2 py-1"><CheckCircle2 className="w-3 h-3" /> CUSTOMIZED</div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-2 h-11 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">HTML Body</label>
              <button onClick={() => setShowPreview(!showPreview)} className="text-xs font-semibold text-[#f7a11a] hover:underline">
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>
            </div>
            <Textarea id="tpl-html" value={html} onChange={(e) => setHtml(e.target.value)} rows={16}
              className="font-mono text-xs rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={save} disabled={loading} className="bg-[#0b2545] hover:bg-[#0b2545]/90 text-white rounded-none h-10 font-semibold text-xs tracking-widest">
              <Save className="w-4 h-4 mr-1.5" /> SAVE TEMPLATE
            </Button>
            <Button onClick={reset} variant="outline" disabled={loading || currentTpl?.is_default} className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-none h-10 font-semibold text-xs tracking-widest">
              <RotateCcw className="w-4 h-4 mr-1.5" /> RESET TO DEFAULT
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 bg-slate-50 p-6 space-y-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#f7a11a] mb-2">Available variables</div>
            <div className="text-[11px] text-slate-500 mb-3">Click a variable to insert it into the HTML body.</div>
            <div className="flex flex-wrap gap-1.5">
              {meta.vars.map((v) => (
                <button key={v} onClick={() => insertVar(v)} className="px-2 py-1 bg-white border border-slate-300 text-slate-800 text-xs font-mono hover:border-[#f7a11a] hover:bg-[#f7a11a]/5">
                  {'{{' + v + '}}'}
                </button>
              ))}
            </div>
          </div>

          {showPreview && (
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#f7a11a] mb-2">Live preview</div>
              <div className="bg-white border border-slate-300 h-[380px] overflow-y-auto">
                <iframe title="preview" srcDoc={html} sandbox="" className="w-full h-full border-0" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN ADMIN ============
const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem('udaan_admin_token') || null);
  const [tab, setTab] = useState('quotes'); // 'quotes' | 'templates'
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, replied: 0, archived: 0 });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const url = `${API}/admin/export${filter !== 'all' ? `?status=${filter}` : ''}`;
      const res = await axios.get(url, { ...authHeaders, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const link = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link;
      const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 13);
      a.download = `Udaan_RFQ_Export_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(link);
      toast({ title: 'Export ready', description: 'ZIP downloaded with Excel + attachments.' });
    } catch (err) {
      toast({ title: 'Export failed', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/admin/quotes${filter !== 'all' ? `?status=${filter}` : ''}`, authHeaders),
        axios.get(`${API}/admin/stats`, authHeaders)
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('udaan_admin_token');
        setToken(null);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (tab === 'quotes') load(); /* eslint-disable-next-line */ }, [token, filter, tab]);

  const logout = () => { localStorage.removeItem('udaan_admin_token'); setToken(null); };

  const filtered = quotes.filter((q) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return q.name.toLowerCase().includes(s) || q.email.toLowerCase().includes(s) || (q.company || '').toLowerCase().includes(s) || (q.product || '').toLowerCase().includes(s);
  });

  const onDetailUpdate = (u) => { setQuotes((prev) => prev.map((q) => q.id === u.id ? u : q)); setSelected(u); load(); };
  const onDetailClose = (deleted) => { setSelected(null); if (deleted) load(); };

  if (!token) return <LoginGate onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-[#0b2545] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#f7a11a] flex items-center justify-center"><span className="text-slate-900 font-black">U</span></div>
            <div className="leading-tight">
              <div className="font-bold text-sm">UDAAN ADMIN</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">RFQ Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <button onClick={() => setTab('quotes')} className={`px-4 h-16 text-xs font-bold tracking-widest ${tab === 'quotes' ? 'text-white border-b-2 border-[#f7a11a]' : 'text-white/60 hover:text-white'}`}>
              <Inbox className="w-4 h-4 inline mr-1.5" /> RFQ INBOX
            </button>
            <button onClick={() => setTab('analytics')} className={`px-4 h-16 text-xs font-bold tracking-widest ${tab === 'analytics' ? 'text-white border-b-2 border-[#f7a11a]' : 'text-white/60 hover:text-white'}`}>
              <BarChart3 className="w-4 h-4 inline mr-1.5" /> ANALYTICS
            </button>
            <button onClick={() => setTab('templates')} className={`px-4 h-16 text-xs font-bold tracking-widest ${tab === 'templates' ? 'text-white border-b-2 border-[#f7a11a]' : 'text-white/60 hover:text-white'}`}>
              <FileCode2 className="w-4 h-4 inline mr-1.5" /> TEMPLATES
            </button>
          </div>
          <div className="flex items-center gap-3">
            {tab === 'quotes' && (
              <button onClick={load} className="p-2 text-white/70 hover:text-white" title="Refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button onClick={logout} className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-white/70 hover:text-[#f7a11a]">
              <LogOut className="w-4 h-4" /> LOGOUT
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {tab === 'quotes' ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total RFQs', value: stats.total, color: 'bg-[#0b2545]', text: 'text-white' },
                { label: 'New', value: stats.new, color: 'bg-[#f7a11a]', text: 'text-slate-900' },
                { label: 'Replied', value: stats.replied, color: 'bg-emerald-600', text: 'text-white' },
                { label: 'Archived', value: stats.archived, color: 'bg-slate-500', text: 'text-white' }
              ].map((s) => (
                <div key={s.label} className={`${s.color} ${s.text} p-6`}>
                  <div className="text-xs tracking-widest uppercase opacity-80">{s.label}</div>
                  <div className="mt-2 text-4xl font-black">{s.value}</div>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200">
              <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 border-b border-slate-200">
                <div className="flex flex-wrap gap-2">
                  {['all', 'new', 'replied', 'archived'].map((f) => (
                    <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 text-xs font-bold tracking-widest uppercase transition-colors ${filter === f ? 'bg-[#0b2545] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                      {f}
                    </button>
                  ))}
                </div>
                <div className="md:ml-auto flex items-center gap-2 w-full md:w-auto">
                  <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, company..." className="pl-10 h-10 rounded-none border-slate-300" />
                  </div>
                  <Button onClick={exportExcel} disabled={exporting || quotes.length === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none h-10 font-bold text-xs tracking-widest whitespace-nowrap">
                    <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                    {exporting ? 'EXPORTING...' : 'EXPORT ZIP'}
                  </Button>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="py-16 text-center text-slate-500">
                  <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  {loading ? 'Loading...' : 'No RFQs found.'}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filtered.map((q) => {
                    const cfg = statusColors[q.status] || statusColors.new;
                    return (
                      <button key={q.id} onClick={() => setSelected(q)} className="w-full text-left px-4 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                        <div className={`w-10 h-10 flex items-center justify-center ${cfg.bg} flex-shrink-0`}>
                          <cfg.icon className={`w-4 h-4 ${cfg.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-900">{q.name}</span>
                            {q.company && <span className="text-xs text-slate-500">· {q.company}</span>}
                            {q.product && <span className="text-xs px-2 py-0.5 bg-[#f7a11a]/10 text-[#f7a11a] font-semibold">{q.product}</span>}
                            {q.attachments && q.attachments.length > 0 && (
                              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 font-semibold inline-flex items-center gap-1">
                                <Paperclip className="w-3 h-3" /> {q.attachments.length}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-slate-600 truncate">{q.message}</div>
                        </div>
                        <div className="text-right flex-shrink-0 hidden md:block">
                          <div className="text-xs text-slate-500">{new Date(q.timestamp).toLocaleDateString()}</div>
                          <div className="text-[10px] text-slate-400">{new Date(q.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : tab === 'analytics' ? (
          <Analytics token={token} />
        ) : (
          <TemplateEditor token={token} />
        )}
      </div>

      {selected && <QuoteDetail quote={selected} token={token} onUpdate={onDetailUpdate} onClose={onDetailClose} />}
    </div>
  );
};

export default Admin;
